import React, {
    memo,
    useCallback,
    useMemo
} from 'react';
import PropTypes from 'prop-types';

import {
    useGetFocusedInstanceIdValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import Empty from './Empty.jsx';
import PanelWithUpsell from './PanelWithUpsell.jsx';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import MetricTableCell from '../../../components/table/MetricTableCell.jsx';
import MetricTableProgressBarCell from '../../../components/table/MetricTableProgressBarCell.jsx';
import RIQWordCloud from '../../../components/RIQWordCloud.jsx';
import TableLayoutSelector from './TableLayoutSelector.jsx';
import useContentModal from '../hooks/useContentModal.jsx';

import {
    useBlueColorPalette
} from '../hooks/useColorPalette.jsx';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

DimensionAnalysisWidget.propTypes = {
    instanceId: PropTypes.string.isRequired,
    className: PropTypes.string,
    dimension: PropTypes.string.isRequired,
    dimensionTitle: PropTypes.string,
    pdfExportConfig: PropTypes.object,
    appSpecificExportConfig: PropTypes.object
};

function dimensionToCategory(dimension) {
    const dimensionCategory = {
        phrase: 'terms',
        person: 'people'
    };

    const category = dimensionCategory[dimension];
    if (!category) {
        return [dimension, 's'].join('');
    }

    return category;
}

function DimensionAnalysisTable({dimension, dimensionTitle, tableColumnMetricRequestOrder, metricResults, metricRequests, onRowClick}) {
    const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);
    const postsDescription = listeningDefinitions.metrics.count;

    const formattersByRequestName = _.mapValues(metricRequests, (request, requestName) => {
        const metricResult = metricResults[requestName];
        return {
            abbreviated: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
            expanded: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded)
        };
    });

    const metricMaxes = {};
    metricMaxes.counts = _.last(_.maxBy(collatedResults, result => _.last(result?.counts))?.counts);

    const countMetricRequestName = _.last(tableColumnMetricRequestOrder);
    const countMetricRequest = metricRequests[countMetricRequestName];
    const countMetricName = RivalIQ.metrics.new.MetricRegistry.getName(countMetricRequest, true);

    // NOTE this class is dependent on the number of columns below: if we add
    // more or remove some these values, or this approach, may need to be
    // revisited
    let firstHeaderColumnClassName = 'tlw-w-1/3';
    if (dimension !== 'author') {
        firstHeaderColumnClassName = 'tlw-w-2/3';
    }

    return (<>
        <table className={`tlw-w-full`}>
            <thead>
                <tr className='tlw-bg-gray-150'>
                    <MetricTableHeader
                        className={firstHeaderColumnClassName}
                        title={dimensionTitle}
                    />
                    {dimension === 'author' &&
                    <>
                        <MetricTableHeader
                            className='tlw-w-1/3'
                            title={`domain`}
                        />
                    </>
                    }
                    <MetricTableHeader
                        key={countMetricRequestName}
                        title={countMetricName}
                        description={postsDescription}
                        alignment={MetricTableHeader.Alignment.RIGHT}
                        sortIndicator={MetricTableHeader.SortIndicator.SORT_DOWN}
                    />

                </tr>
            </thead>

            <tbody>
                {_.map(collatedResults, (columnData) => {
                    const dimensionValueName = (dimension === 'author' ? 'authorName' : 'dimensionValue');
                    const dimensionValue = columnData[dimensionValueName];
                    const metricCellClassName = `tlw-w-full tlw-truncate tlw-text-left`;
                    return (
                        <tr
                            key={dimensionValue[0]}
                            className='tlw-h-[47px] tlw-cursor-pointer tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50'
                            onClick={() => onRowClick(columnData)}>
                            <MetricTableCell
                                className={metricCellClassName}
                                abbreviatedValue={formattersByRequestName[dimensionValueName].abbreviated(dimensionValue[1])}
                                metricName={dimension}
                                alignment={MetricTableCell.Alignment.LEFT}
                            />
                            {dimension === 'author' &&
                            <>
                                <MetricTableCell
                                    className={metricCellClassName}
                                    abbreviatedValue={columnData.authorDomain[1]}
                                    metricName={`domain`}
                                    alignment={MetricTableCell.Alignment.LEFT}
                                />
                            </>
                            }
                            <MetricTableProgressBarCell
                                key={countMetricRequestName}
                                rawValue={_.last(columnData[countMetricRequestName])}
                                maxValue={metricMaxes.counts}
                                abbreviatedValue={formattersByRequestName.counts.abbreviated(_.last(columnData[countMetricRequestName]))}
                                metricName={countMetricName}
                                expandedValue={formattersByRequestName.counts.expanded(_.last(columnData[countMetricRequestName]))}
                                valueClassName='tlw-w-[50px] tlw-text-right'
                            />
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </>);
}

const DimensionWordCloud = memo(function DimensionWordCloud({dimension, metricResults, onWordClick}) {
    const colorPalette = useBlueColorPalette();

    const words = useMemo(() => {
        const dimensionValue = metricResults.dimensionValue.request;
        const dimensionValueFormatter = RivalIQ.metrics.new.MetricFormatter.getFormatter(dimensionValue, metricResults.dimensionValue, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated);
        return _.map(RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults), ({counts}, idx) => {
            return {
                text: dimensionValueFormatter(counts[0]),
                _actualText: counts[0],
                dimension: dimension,
                value: counts[1],
                className: `${colorPalette(idx)} hover_tlw-opacity-60`
            };
        });
    }, [colorPalette, dimension, metricResults]);

    return (
        <RIQWordCloud
            words={words}
            onWordClick={onWordClick}
        />
    );
});

export default function DimensionAnalysisWidget({instanceId, className, dimension, dimensionTitle, allowedWithoutFullInstantSearch, pdfExportConfig, appSpecificExportConfig}) {

    const {
        title,
        description,
        footer: footerContextView
    } = listeningDefinitions[instanceId] || {};

    dimensionTitle = dimensionTitle || title || dimension;

    const metricFilters = useListeningCurationFilterValues(instanceId);
    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });


    const layout = useCurationConfigValue(instanceId, 'layout');

    const focusedInstanceId = useGetFocusedInstanceIdValue();

    const {showContentModal, ContentModal} = useContentModal(`${instanceId}-${dimension}`);

    const summaryLimit = (layout === 'wordcloud' ? 50 : 8);
    const viewMoreLimit = (layout === 'wordcloud' ? 100 : -1);
    const rowLimit = (focusedInstanceId === instanceId ? viewMoreLimit : summaryLimit);
    metricFilters.listening.limit = rowLimit;
    const metricRequests = {
        dimensionValue: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [dimension],
            measure: {
                field: 'count',
                returns: `dimensionValue(${dimension})`
            },
            filters: metricFilters
        },
        counts: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [dimension],
            measure: 'count',
            filters: metricFilters
        }
    };

    if (instanceId === 'author-analysis') {
        metricRequests.authorDomain = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [dimension],
            measure: 'authorDomain',
            filters: metricFilters
        };
        metricRequests.authorHandle = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [dimension],
            measure: 'authorHandle',
            filters: metricFilters
        };
        metricRequests.authorName = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [dimension],
            measure: 'authorName',
            filters: metricFilters
        };
    }

    // This is an array of keys into the metricRequests object above to control the order of the columns in the table.
    const tableColumnMetricRequestOrder = (dimension === 'author' ? ['authorName', 'authorDomain', 'counts'] : ['dimensionValue', 'counts']);

    useSetExportConfig(instanceId, {
        title: `${dimensionTitle}`,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            metricGroups: [
                _.map(tableColumnMetricRequestOrder, metricRequestName => {
                    const metricRequestGroup = JSON.parse(JSON.stringify(metricRequests[metricRequestName]));
                    metricRequestGroup.filters.listening.limit = -1;
                    return metricRequestGroup;
                })
            ]
        },
        appSpecificExportConfig
    });

    function onRowClick(columnData) {
        let rowClickData = columnData.counts[0];
        // author is a special case, when retrieving all information, we need the triple key to display the table
        // when searching for one specific author, we need only two parameters handle/name : domain
        // default to handle:domain but when handle is blank, fall back to authorName:domain
        let titleOverride = null;
        if (dimension === 'author') {
            rowClickData = `${(columnData.authorHandle[1] === '' ? columnData.authorName[1] : columnData.authorHandle[1])}:${columnData.authorDomain[1]}`;
            if (columnData.authorHandle[1] !== '') {
                titleOverride = `@${columnData.authorHandle[1]}:${columnData.authorDomain[1]}`;
            }
        } else if (dimension === 'domain') {
            rowClickData = [rowClickData];
        }

        showContentModal({
            [dimension]: rowClickData
        },
        titleOverride,
        columnData.counts[1]);
    }

    const onWordClick = useCallback((word) => {
        showContentModal({
            [dimension]: word._actualText
        }, null, word.value);
    }, [dimension, showContentModal]);

    const additionalTitleBar = `${instanceId}` === 'domain-analysis' || `${instanceId}` === 'author-analysis' ? null : <TableLayoutSelector instanceId={instanceId}/>;

    return (
        <PanelWithUpsell
            instanceId={instanceId}
            title={`${dimensionTitle}`}
            description={description}
            exportSubtitle={appSpecificExportConfig?.timePeriodStr}
            showViewMore={true}
            className={className}
            additionalTitleBarControls={additionalTitleBar}
            footerContextView={footerContextView}
            allowedWithoutFullInstantSearch={allowedWithoutFullInstantSearch}
        >
            <MetricLoadingView
                emptyFallback={<Empty subject={_.lowerCase(dimensionToCategory(dimension))}/>}
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {
                        if (layout === 'wordcloud') {
                            return (
                                <DimensionWordCloud
                                    dimension={dimension}
                                    metricResults={metricResults}
                                    onWordClick={onWordClick}
                                />
                            );
                        }

                        return (
                            <DimensionAnalysisTable
                                dimension={dimension}
                                dimensionTitle={dimensionTitle}
                                tableColumnMetricRequestOrder={tableColumnMetricRequestOrder}
                                metricResults={metricResults}
                                metricRequests={metricRequests}
                                onRowClick={onRowClick}
                            />
                        );
                    }
                }
            </MetricLoadingView>

            {ContentModal}
        </PanelWithUpsell>
    );
}
